import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';

const categories = [
  { name: 'Film', slug: 'film' },
  { name: 'Music', slug: 'music' },
  { name: 'TV', slug: 'tv' },
  { name: 'Sports', slug: 'sports' },
  { name: 'Business', slug: 'business' },
];

export const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="container-custom">
        {/* Main Header */}
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px]">
              <nav className="flex flex-col gap-4 mt-8">
                <Link 
                  to="/" 
                  className="text-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-home-link"
                >
                  Home
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="text-lg font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-category-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                ))}
                <div className="border-t pt-4 mt-4">
                  <Link
                    to="/submit"
                    className="text-lg font-medium text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-submit-link"
                  >
                    Submit Content
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/ti47qc52_image1.webp" 
              alt="The Trade" 
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="font-mono text-sm uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`nav-category-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <Input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-40 md:w-64 h-9"
                  autoFocus
                  data-testid="search-input"
                />
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setSearchOpen(false)}
                  data-testid="close-search-btn"
                >
                  <X className="h-5 w-5" />
                </Button>
              </form>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setSearchOpen(true)}
                  data-testid="open-search-btn"
                >
                  <Search className="h-5 w-5" />
                </Button>
                <Link to="/submit" className="hidden md:block">
                  <Button variant="default" size="sm" data-testid="submit-btn">
                    Submit
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Mobile Category Chips */}
        <div className="md:hidden pb-3 -mx-4 px-4">
          <div className="horizontal-scroll">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-2 bg-secondary rounded-sm text-sm font-medium whitespace-nowrap"
                data-testid={`chip-category-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
